import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import Backdrop from '@mui/material/Backdrop';
import dayjs from 'dayjs';
import { useContext, useState } from 'react';
import { Modal, OverlayTrigger, Popover, PopoverContent, Spinner } from 'react-bootstrap';
import information from '../../../images/information.svg';
import AdditionalEPOFileUpload from '../../../legacy-components/AdditionalEPOFileUpload/AdditionalEPOFileUploadContainer';
import { isLoadStatusSubmitted } from '../../../utils/unifiedLoads';
import ConfirmationModal from '../../ConfirmationModal';
import DataTable from '../../DataTable';
import { useSecurity } from '../../Security/security';
import { StoreContext } from '../../Store';
import { QAStoreContext } from '../QAChecklistTab/QAStore';
import { useDocumentActions, useDocumentTable } from '../queryHooks/useDocuments';
import CustomaiteModal from './CustomaiteModal';
import Tooltip from '../../../components/Tooltip';
import useDocumentViewer from './DocumentViewer/useDocumentViewer';

const modalTypes = {
  none: '',
  upload: 'upload',
  customaite: 'customaite',
  confirmRpa: 'confirmRpa',
  rpaFailed: 'rpaFailed',
  cm8Success: 'cm8Success',
};

const rpaFileTypes = ['.CSV', '.XLS', '.XLSX', '.XML', '.JSON'];

const DocumentsTable = ({ setAlertOpen, setErrorMessage, setShowCm8Submission, route }) => {
  const store = useContext(StoreContext);
  const qaStore = useContext(QAStoreContext);
  const loadId = store.loadId[0];
  const [processedRpaId, setProcessedRpaId] = store.processedRpaId;
  const isLoadSubmitted = isLoadStatusSubmitted(qaStore.unifiedLoadData[0]?.Headers?.[0]?.loadstatus);

  const { validateRole } = useSecurity();

  const { data: documentsList = [], isLoading, refetch } = useDocumentTable(loadId);
  const { uploadDocument, deleteDocument, restoreDocument, downloadAllDocuments, processRpa, submitToCustomaite } =
    useDocumentActions(loadId);

  const isUpdateActionPending =
    restoreDocument.isPending || deleteDocument.isPending || processRpa.isPending || submitToCustomaite.isPending;

  const [modal, setModal] = useState(modalTypes.none);
  const [selectedRpaFile, setSelectedRpaFile] = useState(null);
  const [failedRpaIds, setFailedRpaIds] = useState([]);
  const [failedCm8Ids, setFailedCm8Ids] = useState([]);

  const handleDocumentSaved = ({ id, invoiceNumber, documents }) => {
    if (isLoadSubmitted) return;

    const invoiceReferences = documents.map((document) => document.invoiceNumber?.trim()).filter(Boolean);
    const hasDifferentInvoiceReferences = new Set(invoiceReferences).size > 1;

    if (!hasDifferentInvoiceReferences) {
      if (invoiceNumber || !store.invoicenumber[0]) {
        store.invoicenumber[1](invoiceNumber || '');
      }
      store.newLoadDataModified[1](true);
      return;
    }

    const multipleConsignorRoute = ['ROI - GB', 'ROI - GB via NI', 'EU (mainland) - GB', 'ROW - GB'].includes(route);
    const multipleInvoiceRoute = ['GB - ROI', 'GB - ROW', 'GB - EU', 'GB - EU (mainland)', 'GB - ROI via NI'].includes(
      route
    );

    if (multipleConsignorRoute) {
      store.multipleConsignors[1](true);
      store.multipleInvoices[1](true);
    } else if (multipleInvoiceRoute) {
      store.multipleInvoices[1](true);
    }

    store.invoicenumber[1]('');
    store.newLoadDataModified[1](true);
  };
  const { openDocumentViewer, disconnectDocumentViewer } = useDocumentViewer({
    refetchDocuments: refetch,
    onDocumentSaved: handleDocumentSaved,
  });

  const { loadstatus: epoLoadStatus, loadType } = qaStore.unifiedLoadData[0]?.Headers?.[0] || {};
  const isEpoStatusAbleToBeSubmittedToCm8 = [
    'New',
    'Draft',
    'On Hold (Potential Duty)',
    'Waiting for Customer',
    'CM8 Draft',
  ].some((status) => status === epoLoadStatus || status === store.loadStatus[0]);

  const hasADocumentBeenSubmittedToCm8 =
    submitToCustomaite.isSuccess ||
    documentsList.some((document) => document.status === 'CM8 Submitted' || document.status === 'CM8 Processed');

  const visibleDocuments = documentsList.filter(({ isDeleted }) => !isDeleted);

  const header = qaStore.unifiedLoadData[0]?.Headers?.[0] ?? {};
  const savedItems = qaStore.unifiedLoadData[0]?.Items;
  const hasItems =
    (Array.isArray(savedItems) && savedItems.length > 0) ||
    (Array.isArray(store.validatedRowsReadyToSubmit[0]) && store.validatedRowsReadyToSubmit[0].length > 0);
  const hasHeader =
    !!store.directionSelected[0] ||
    !!store.jobRef[0] ||
    !!store.invoicenumber[0] ||
    !!header.movementTypeId ||
    !!header.jobRef ||
    !!header.InvoiceNumber ||
    !!header.InvoiceNumberV2;
  const loadHasExistingInputData = hasHeader || hasItems;

  const confirmRpa = async ({ id, filename }) => {
    try {
      const response = await processRpa.mutateAsync({
        fileId: id,
        filename: filename,
      });

      if (response.success) {
        setFailedRpaIds((prev) => prev.filter((rpaId) => rpaId !== id));
        setProcessedRpaId(id);

        const rpaData = response.data;

        //Keep the current load id otherwise the RPA response would think that this was a new load
        rpaData.Headers[0].id = loadId;
        rpaData.Headers[0].portalRef = store.portalRef[0];

        store.newLoadFileData[1](rpaData);
        store.newLoadFromFile[1](true);
      } else {
        throw new Error(response?.message || 'Unable to process RPA');
      }
    } catch {
      setFailedRpaIds((prev) => [...prev, id]);
      setModal(modalTypes.rpaFailed);
    }
  };

  const onCm8Click = (e) => {
    e.stopPropagation();
    if (!store.jobRef[0] || !store.directionSelected[0] || !store.jiraTicket[0] || !store.date[0]) {
      setAlertOpen(true);
      setErrorMessage(`Please fill all the required fields: ${getCm8MissingFields()}`);
    } else {
      setModal(modalTypes.customaite);
    }
  };

  const getCm8MissingFields = () => {
    let missingFieldNames = [];
    !store.directionSelected[0] && missingFieldNames.push('Route');
    !store.jobRef[0] && missingFieldNames.push('Job Reference');
    !store.jiraTicket[0] && missingFieldNames.push('Jira Ticket');
    !store.date[0] && missingFieldNames.push('Date of Loading');

    return missingFieldNames.join(', ');
  };

  const onCm8Submit = async (ids) => {
    const emptyToNull = (value) => (!value ? null : value);

    const stringToNumber = (value) => {
      if (!value) return null;
      const num = Number(value);
      return isNaN(num) ? null : num;
    };
    try {
      const payload = {
        ids,
        invoicenumber: emptyToNull(store.invoicenumber[0]),
        jobref: emptyToNull(store.jobRef[0]),
        date: emptyToNull(store.date[0]),
        direction: emptyToNull(store.directionSelected[0]),
        companyId: emptyToNull(store.clientID[0]),
        newLoadId: emptyToNull(store.loadId[0]),
        countrydespatch: emptyToNull(store.despatch[0]),
        countrydestination: emptyToNull(store.destination[0]),
        exitport1: store.RowImport[0] ? null : emptyToNull(store.exit[0]),
        exitport2: store.RowImport[0] ? null : emptyToNull(store.exit2[0]),
        entryport1: store.RowExport[0] ? null : emptyToNull(store.entry[0]),
        entryport2: store.RowExport[0] ? null : emptyToNull(store.entry2[0]),
        jiraTicket: emptyToNull(store.jiraTicket[0]),
        consignor: emptyToNull(store.consignor[0]),
        consignee: emptyToNull(store.consignee[0]),
        invoiceCurr: stringToNumber(emptyToNull(store.invoicecurr[0])),
        truck: emptyToNull(store.truck[0]),
        trailerSeal: emptyToNull(store.trailerSeal[0]),
        officeofDest: emptyToNull(store.officeOfDest[0]),
        shipStatus: stringToNumber(emptyToNull(store.shipStatus[0])),
        incotermcode: emptyToNull(store.selectincoterm[0]),
        controlledGoods: emptyToNull(store.controlledGoods[0]),
        portModal: emptyToNull(store.portsModal[0]),
        container: emptyToNull(store.container[0]),
        incotermLocation: emptyToNull(store.incotermLocation?.[0]),
      };

      const cleanPayload = (payload) => {
        return Object.entries(payload).reduce((acc, [key, value]) => {
          acc[key] = value === 'null' || value === null ? null : value;
          return acc;
        }, {});
      };

      await submitToCustomaite.mutateAsync(cleanPayload({ ...payload }));

      setShowCm8Submission();

      refetch();
      // ‘ROI-GB’, ‘ROI-GB via NI’, ‘EU-GB’ or ‘ROW-GB’
      if ([2, 13, 11, 10].includes(store.directionSelected[0])) {
        store.multipleConsignors[1](true);
        store.multipleInvoices[1](true);
      }

      setFailedCm8Ids((prev) => prev.filter((failedId) => !ids.includes(failedId)));

      setModal(modalTypes.none);
    } catch (error) {
      setFailedCm8Ids((prev) => Array.from(new Set([...prev, ...ids])));
      console.error(error);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between p-4">
        <h3>Document(s)</h3>
        <button
          type="button"
          className="blue-button-newload transparent width-fit"
          onClick={() =>
            downloadAllDocuments.mutate({
              loadId: store.loadId[0],
              filename: `${store.portalRef[0] || 'load'}-documents.zip`,
            })
          }
          disabled={isUpdateActionPending || downloadAllDocuments.isPending}
        >
          Download All <DownloadIcon />
        </button>
      </div>
      <div className="px-3 pt-4">
        <DataTable data={documentsList} isLoading={isLoading} className="table-bordered">
          <DataTable.Row style={{ fontSize: '14px' }} />

          <DataTable.Column colKey="documentType" header="document type" />
          <DataTable.Column colKey="filename" header="filename" />
          <DataTable.Column
            colKey="totalRows"
            header="total rows"
            style={{ textAlign: 'center', width: '130px' }}
            headerStyle={{ textAlign: 'center', width: '130px' }}
          >
            {({ totalRows }) => totalRows ?? '-'}
          </DataTable.Column>
          <DataTable.Column colKey="status" header=" status">
            {({ id, status }) => {
              if (processedRpaId === id) {
                return 'RPA Processed';
              }
              if (failedRpaIds.includes(id)) {
                return 'RPA Failed';
              }
              if (failedCm8Ids.includes(id)) {
                return 'CM8 Failed';
              }

              return status;
            }}
          </DataTable.Column>
          <DataTable.Column colKey="lastModifiedUser" header="lastModified User" />
          <DataTable.Column
            colKey="cm8"
            header="cm8"
            style={{ textAlign: 'center', width: '24px' }}
            headerStyle={{ textAlign: 'center', width: '24px' }}
          >
            {({ cm8 }) => {
              return cm8 ? (
                <Tooltip title="Selected for Customaite">
                  <CheckBoxOutlinedIcon color="success" titleAccess="This is a customaite document" />
                </Tooltip>
              ) : (
                ''
              );
            }}
          </DataTable.Column>
          <DataTable.Column header="actions" style={{ width: '150px' }} headerStyle={{ width: '150px' }}>
            {({ id, filename, totalRows, cm8, documentType, isDeleted, lastModified, status }) => {
              const showDeleteRestore =
                validateRole({
                  or: [
                    { department: 'CMT', role: 'SUPERUSER' },
                    { department: 'OPS', role: 'SUPERUSER' },
                    { department: 'QA', role: 'SUPERUSER' },
                  ],
                }) &&
                !['CM8 Draft', 'CM8 Submitted', 'CM8 Processed', 'RPA Processed'].includes(status) &&
                processedRpaId !== id &&
                !totalRows;

              return (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <Tooltip title="View document">
                    <button
                      type="button"
                      className="external-table-link p-0 width-fit"
                      onClick={() => {
                        openDocumentViewer(loadId, store.portalRef[0], id, filename, visibleDocuments);
                      }}
                      aria-label="View document"
                      disabled={isDeleted}
                    >
                      <OpenInNewOutlinedIcon color="action" titleAccess="View document" aria-hidden="true" />
                    </button>
                  </Tooltip>
                  <div style={{ width: 42 }}>
                    {rpaFileTypes.some((type) => filename.toUpperCase().endsWith(type)) &&
                      documentType === 'Commercial Document' && (
                        <Tooltip title="Process as RPA">
                          <button
                            type="button"
                            className="external-table-link p-0 width-fit"
                            onClick={() => {
                              setSelectedRpaFile({ id, filename, lastModified });
                              if (
                                processedRpaId ||
                                documentsList.some((document) => document.status === 'RPA Processed') ||
                                loadHasExistingInputData
                              ) {
                                setModal(modalTypes.confirmRpa);
                              } else {
                                confirmRpa({ id, filename });
                              }
                            }}
                            disabled={isDeleted || isLoadSubmitted}
                            aria-label="Process as RPA"
                            title={isDeleted || isLoadStatusSubmitted ? '' : 'Process as RPA'}
                          >
                            <div className="rounded-pill bg-success text-light px-2" aria-hidden="true">
                              RPA
                            </div>
                          </button>
                        </Tooltip>
                      )}
                  </div>
                  {showDeleteRestore && (
                    <>
                      {isDeleted ? (
                        <Tooltip title="Restore document">
                          <button
                            type="button"
                            className="external-table-link p-0 width-fit"
                            onClick={() => {
                              restoreDocument.mutate(
                                { fileId: id },
                                { onSuccess: () => disconnectDocumentViewer(loadId) }
                              );
                            }}
                            aria-label="Restore document"
                            disabled={isLoadSubmitted || isUpdateActionPending}
                          >
                            <SettingsBackupRestoreIcon
                              color="primary"
                              titleAccess="Restore document"
                              aria-hidden="true"
                            />
                          </button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Delete document">
                          <button
                            type="button"
                            className="external-table-link p-0 width-fit"
                            onClick={() => {
                              deleteDocument.mutate(
                                { fileId: id },
                                { onSuccess: () => disconnectDocumentViewer(loadId) }
                              );
                            }}
                            aria-label="Delete document"
                            disabled={isLoadSubmitted || isUpdateActionPending || cm8}
                          >
                            <DeleteIcon color="error" titleAccess="Delete document" aria-hidden="true" />
                          </button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              );
            }}
          </DataTable.Column>
        </DataTable>
      </div>
      <div className="mt-2 d-flex justify-content-between p-4">
        <div>
          <button
            type="button"
            className="blue-button-newload width-fit me-2"
            onClick={() => setModal(modalTypes.upload)}
          >
            Upload Document
          </button>
        </div>

        {isEpoStatusAbleToBeSubmittedToCm8 && (
          <OverlayTrigger
            placement="top"
            popperConfig={{
              modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
            }}
            trigger={hasADocumentBeenSubmittedToCm8 || loadType !== 'EPO' ? ['hover', 'focus'] : []}
            overlay={
              <Popover style={{ minWidth: '15rem' }}>
                <PopoverContent>
                  {hasADocumentBeenSubmittedToCm8 ? (
                    <p>Documents have already been sent to Customaite.</p>
                  ) : (
                    <p>Only EPO loads can have documents submitted to Customaite from here.</p>
                  )}
                </PopoverContent>
              </Popover>
            }
          >
            <button
              type="button"
              className="blue-button-newload transparent width-fit"
              onClick={onCm8Click}
              disabled={hasADocumentBeenSubmittedToCm8 || loadType !== 'EPO'}
            >
              Submit to Customaite
            </button>
          </OverlayTrigger>
        )}
      </div>

      <Modal
        show={modal === modalTypes.upload}
        onHide={() => setModal(modalTypes.none)}
        size="md"
        dialogClassName="modal-no-scroll-x"
      >
        <AdditionalEPOFileUpload
          handleBack={() => setModal(modalTypes.none)}
          onUploadSuccess={async () => {
            await refetch();
            setModal(modalTypes.none);
          }}
          uploadFunction={(formData) => uploadDocument.mutate(formData)}
          isUnifiedLoad
        />
      </Modal>
      <Modal
        show={modal === modalTypes.rpaFailed}
        onHide={() => setModal(modalTypes.none)}
        size="lg"
        dialogClassName="modal-no-scroll-x"
        centered
      >
        <h1 className="floating-box-header-newload">
          <span>Submit load file</span>
        </h1>
        <div id="load-info-form" className="fileloader-header">
          <div className="inputs-group">
            <span className="more-info">
              <img src={information} height="" width="30" alt="info" />
              &nbsp;&nbsp;
              <span>We could not process your file, please review the file and try again.</span>
            </span>
          </div>
          <div className="inputs-group">
            <p className="section-title">File name</p>
            <p className="text-break">{selectedRpaFile?.filename}</p>
            <p className="section-title">File type</p>
            <p>{String(selectedRpaFile?.filename).toLowerCase().split('.').slice(-1)}</p>
            <p className="section-title">File last modified</p>
            {dayjs(selectedRpaFile?.lastModified).format('DD/MM/YYYY')}
            <div>
              <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'rgb(255, 30, 30)' }}>
                <button
                  type="button"
                  className="blue-button-newload file-loader-btn"
                  onClick={() => setModal(modalTypes.none)}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <CustomaiteModal
        show={modal === modalTypes.customaite}
        onHide={() => {
          setModal(modalTypes.none);
          //clear error for next time the modal opens
          submitToCustomaite.reset();
        }}
        onSubmit={(ids) => onCm8Submit(ids)}
        documentsList={documentsList.filter(({ cm8 }) => cm8)}
        loading={submitToCustomaite.isPending}
        error={submitToCustomaite.error}
      />
      <ConfirmationModal
        show={modal === modalTypes.confirmRpa}
        onHide={() => setModal(modalTypes.none)}
        onConfirm={() => confirmRpa(selectedRpaFile)}
        centered
      >
        Processing this file as RPA will overwrite existing header and item data on this load. Do you want to continue?
      </ConfirmationModal>

      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={processRpa.isPending}>
        <Spinner animation="border" size="lg" />
      </Backdrop>
    </div>
  );
};

export default DocumentsTable;
